import { useAuth } from '@/providers/auth.provider'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'
import {
  getProductFieldPermission,
  getProductSetting,
} from './ProductTransform'
import type {
  ProductFieldAccess,
  ProductFieldPermissionRecord,
  ProductFieldPermissionTarget,
} from './ProductFieldPermission.types'
import { resolveProductFieldAccess } from './ProductFieldPermission.utils'

type ProductFieldPermissionContextValue = {
  getFieldAccess: (target: ProductFieldPermissionTarget) => ProductFieldAccess
  isLoading: boolean
}

const ProductFieldPermissionContext =
  createContext<ProductFieldPermissionContextValue | null>(null)

export function shouldRefetchProductFieldPermission(pathname: string) {
  return /^\/products\/(add|[^/]+(\/edit)?)$/.test(pathname)
}

function getUserRoleCode(user: any) {
  return (
    user?.subscription?.role_access?.role ??
    user?.role_access?.role ??
    user?.subscriptions?.[0]?.role_access?.role
  )
}

export function ProductFieldPermissionProvider({
  children,
}: PropsWithChildren<{}>) {
  const { user } = useAuth()
  const location = useLocation()
  const previousPathnameRef = useRef(location.pathname)
  const roleCode = getUserRoleCode(user)
  const companyId = user?.company?.id
  const isAdmin = roleCode?.trim().toLowerCase() === 'admin'
  const settingQuery = useQuery('GET_PRODUCT_SETTINGS', getProductSetting, {
    enabled: !isAdmin,
  })
  const permissionQuery = useQuery<ProductFieldPermissionRecord[]>(
    ['GET_PRODUCT_FIELD_PERMISSION', user?.id, roleCode, companyId],
    getProductFieldPermission,
    {
      cacheTime: 0,
      enabled: !isAdmin,
      refetchOnMount: 'always',
      staleTime: 0,
    }
  )
  const refetchFieldPermission = permissionQuery.refetch
  useEffect(() => {
    const previousPathname = previousPathnameRef.current
    previousPathnameRef.current = location.pathname

    if (previousPathname === location.pathname) return
    if (isAdmin) return
    if (!shouldRefetchProductFieldPermission(location.pathname)) return

    refetchFieldPermission()
  }, [isAdmin, location.pathname, refetchFieldPermission])

  const permissions = permissionQuery.data ?? []
  const getFieldAccess = useCallback(
    (target: ProductFieldPermissionTarget) =>
      isAdmin
        ? 'write'
        : resolveProductFieldAccess({
            ...target,
            companyId,
            hasManualSource: settingQuery.data?.hasManualSource === true,
            permissions,
            roleCode,
          }),
    [
      companyId,
      isAdmin,
      permissions,
      roleCode,
      settingQuery.data?.hasManualSource,
    ]
  )
  const value = useMemo(
    () => ({
      getFieldAccess,
      isLoading:
        !isAdmin && (settingQuery.isLoading || permissionQuery.isFetching),
    }),
    [
      getFieldAccess,
      isAdmin,
      permissionQuery.isFetching,
      settingQuery.isLoading,
    ]
  )

  return (
    <ProductFieldPermissionContext.Provider value={value}>
      {children}
    </ProductFieldPermissionContext.Provider>
  )
}

export function useProductFieldPermission() {
  const context = useContext(ProductFieldPermissionContext)

  if (!context) {
    throw new Error(
      'useProductFieldPermission must be used within ProductFieldPermissionProvider'
    )
  }

  return context
}
